import { useMemo, useState } from 'react';
import { formatDuration, formatHours } from '@chronos/shared';
import type { TimeEntry } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useMyProjects, useTimerActions, useTimesheet } from '@/hooks/queries';
import { Card, ConfirmButton, Dot, EmptyState, ErrorNote, Modal, PageHeader, Spinner, StatTile, SubmitButton } from '@/components/ui';
import { addDays, formatDate, formatTime, startOfWeek, toDateInput, toDateTimeInput } from '@/lib/format';
import { ManualEntryModal } from '@/pages/TrackerPage';

export function TimesheetPage() {
  const { settings } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek());
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [adding, setAdding] = useState(false);

  const from = weekStart.toISOString();
  const to = addDays(weekStart, 7).toISOString();
  const { data, isLoading, error } = useTimesheet(from, to);
  const { remove } = useTimerActions();

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const secondsByDay = useMemo(() => new Map((data?.byDay ?? []).map((d) => [d.date, d.seconds])), [data]);

  const entriesByDay = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const e of data?.entries ?? []) {
      const key = toDateInput(new Date(e.startTime));
      map.set(key, [...(map.get(key) ?? []), e]);
    }
    return map;
  }, [data]);

  const maxSeconds = Math.max(1, ...days.map((d) => secondsByDay.get(toUtcKey(d)) ?? 0));
  const isThisWeek = toDateInput(weekStart) === toDateInput(startOfWeek());

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="My timesheet"
        description="Everything you've tracked, week by week."
        actions={
          <div className="flex flex-wrap items-center gap-1">
            <button className="btn-secondary" onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">←</button>
            <button className="btn-secondary" onClick={() => setWeekStart(startOfWeek())} disabled={isThisWeek}>This week</button>
            <button className="btn-secondary" onClick={() => setWeekStart(addDays(weekStart, 7))} disabled={isThisWeek} aria-label="Next week">→</button>
            {settings?.allowManualTimeEntry !== false && (
              <button className="btn-primary ml-2" onClick={() => setAdding(true)}>Add time manually</button>
            )}
          </div>
        }
      />

      <p className="mb-4 text-sm text-slate-500">
        {formatDate(weekStart)} – {formatDate(addDays(weekStart, 6))}
      </p>

      <ErrorNote error={error} />

      <ManualEntryModal open={adding} onClose={() => setAdding(false)} />

      <div className="grid gap-4 md:grid-cols-3">
        <StatTile label="Week total" value={formatHours(data?.totalSeconds ?? 0)} />
        <StatTile label="Daily average" value={formatHours(Math.round((data?.totalSeconds ?? 0) / 7))} hint="across 7 days" />
        <StatTile label="Projects" value={String(data?.byProject.length ?? 0)} hint="worked on this week" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-800">Hours per day</h2>
          </div>
          <div className="flex h-52 items-end gap-2 px-4 py-4">
            {days.map((d) => {
              const seconds = secondsByDay.get(toUtcKey(d)) ?? 0;
              return (
                <div key={d.toISOString()} className="flex flex-1 flex-col items-center gap-1.5">
                  <span className="text-xs tabular-nums text-slate-500">{seconds ? (seconds / 3600).toFixed(1) : ''}</span>
                  <div
                    className="w-full rounded-t bg-brand-500 transition-all"
                    style={{ height: `${Math.max(seconds ? 4 : 0, (seconds / maxSeconds) * 130)}px` }}
                    title={formatHours(seconds)}
                  />
                  <span className="text-xs text-slate-500">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-800">By project</h2>
          </div>
          {data?.byProject.length ? (
            <ul className="divide-y divide-slate-100">
              {data.byProject.map((p) => (
                <li key={p.projectId} className="flex items-center gap-2.5 px-4 py-2.5">
                  <Dot color={p.color} />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{p.projectName}</span>
                  <span className="font-mono text-sm tabular-nums text-slate-600">{formatHours(p.seconds)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Nothing tracked this week" />
          )}
        </Card>
      </div>

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Entries</h2>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (data?.entries.length ?? 0) === 0 ? (
          <EmptyState title="No entries in this week" description="Start the timer, or add time manually from the Timer page." />
        ) : (
          <div className="divide-y divide-slate-100">
            {days.map((d) => {
              const key = toDateInput(d);
              const entries = entriesByDay.get(key) ?? [];
              if (!entries.length) return null;
              const dayTotal = entries.reduce((s, e) => s + (e.durationSeconds ?? 0), 0);
              return (
                <div key={key}>
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{formatDate(d)}</span>
                    <span className="font-mono text-xs tabular-nums text-slate-600">{formatHours(dayTotal)}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {entries.map((e) => (
                      <li key={e.id} className="flex items-center gap-3 px-4 py-2.5">
                        <Dot color={e.projectColor ?? '#94a3b8'} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-slate-800">{e.projectName}</p>
                          <p className="truncate text-xs text-slate-500">{e.description || 'No description'}</p>
                        </div>
                        <span className="hidden text-xs text-slate-500 sm:block">
                          {formatTime(e.startTime)} – {e.endTime ? formatTime(e.endTime) : 'running'}
                        </span>
                        {e.isManual && <span className="badge bg-amber-100 text-amber-800">manual</span>}
                        <span className="badge bg-slate-100 text-slate-600">{e.source.toLowerCase()}</span>
                        <span className="w-20 text-right font-mono text-sm tabular-nums text-slate-700">{formatDuration(e.durationSeconds ?? 0)}</span>
                        {settings?.allowManualTimeEntry !== false && e.endTime && (
                          <div className="flex gap-1">
                            <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setEditing(e)}>Edit</button>
                            <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Delete" confirmLabel="Confirm" onConfirm={() => remove.mutate(e.id)} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {editing && <EditEntryModal key={editing.id} entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** The API buckets days in UTC; match that when reading `byDay`. */
function toUtcKey(d: Date): string {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().slice(0, 10);
}

function EditEntryModal({ entry, onClose }: { entry: TimeEntry; onClose: () => void }) {
  const projects = useMyProjects();
  const { update } = useTimerActions();
  // Mounted with a key of the entry id, so initialising from props is enough.
  const [form, setForm] = useState({
    projectId: entry.projectId,
    description: entry.description ?? '',
    startTime: toDateTimeInput(new Date(entry.startTime)),
    endTime: entry.endTime ? toDateTimeInput(new Date(entry.endTime)) : '',
  });

  return (
    <Modal
      open
      title="Edit time entry"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <SubmitButton form="edit-entry-form" pending={update.isPending} className="btn-primary">Save changes</SubmitButton>
        </>
      }
    >
      <form
        id="edit-entry-form"
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(
            {
              id: entry.id,
              projectId: form.projectId,
              description: form.description.trim() || null,
              startTime: new Date(form.startTime).toISOString(),
              endTime: form.endTime ? new Date(form.endTime).toISOString() : null,
            },
            { onSuccess: onClose },
          );
        }}
      >
        <ErrorNote error={update.error} />
        <div>
          <label className="label" htmlFor="eproject">Project</label>
          <select id="eproject" className="input" value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="estart">Start</label>
            <input id="estart" type="datetime-local" className="input" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} required />
          </div>
          <div>
            <label className="label" htmlFor="eend">End</label>
            <input id="eend" type="datetime-local" className="input" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="edesc">Description</label>
          <input id="edesc" className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} />
        </div>
      </form>
    </Modal>
  );
}
